import {
  DEFAULT_BILLABLE_DAYS_PER_MONTH,
  DEFAULT_INTERMEDIAIRE_MARGIN_PCT,
  normalizePctInputToRate,
  parseLocaleNumber,
} from "@/lib/businessRules";

const MONTHS_IN_YEAR = 12;
const CDI_COST_MULTIPLIER = 1.5;
const CDI_WORKING_DAYS = 210;

const clampNumber = (value, fallback = 0) => {
  const numericValue = parseLocaleNumber(value);
  return numericValue === null ? fallback : numericValue;
};



const computeCommissionAmount = (amount, pct) => {
  const baseAmount = Math.max(0, parseLocaleNumber(amount) ?? 0);
  const resolvedPct = Math.max(0, normalizePctInputToRate(pct, 0) ?? 0);
  return baseAmount * resolvedPct;
};

const computeNetAmount = (amount, pct) => {
  const baseAmount = Math.max(0, parseLocaleNumber(amount) ?? 0);
  return Math.max(0, baseAmount - computeCommissionAmount(baseAmount, pct));
};

const computeCdiDailyCost = (annualSalary) =>
  (Math.max(0, clampNumber(annualSalary, 52000)) * CDI_COST_MULTIPLIER) / CDI_WORKING_DAYS;

/****
 * UI-only sandbox projection helper.
 * This is intentionally a local preview for immediate UX feedback and is NOT
 * the source of truth for official KPI values. Official simulation metrics come
 * from backend /api/admin/pilotage/simulation/compute.
 */
export const computeSimulationSandboxPreview = ({
  mode,
  tarifClient,
  daysPerMonth = DEFAULT_BILLABLE_DAYS_PER_MONTH,
  projectionMonths = MONTHS_IN_YEAR,
  intermediaryPct = 0,
  tace = 1,
  costPerDay,
  salaireAnnuel,
  tjmConsultant,
} = {}) => {
  const safeMode = mode === "CDI" ? "CDI" : "FREELANCE";
  const safeTarifClient = Math.max(0, parseLocaleNumber(tarifClient) ?? 0);
  const safeDaysPerMonth = Math.max(0, parseLocaleNumber(daysPerMonth) ?? 0);
  const safeProjectionMonths = Math.min(
    MONTHS_IN_YEAR,
    Math.max(1, Math.trunc(parseLocaleNumber(projectionMonths) ?? MONTHS_IN_YEAR)),
  );
  const safeIntermediaryPct = Math.max(
    0,
    normalizePctInputToRate(intermediaryPct, DEFAULT_INTERMEDIAIRE_MARGIN_PCT) ?? DEFAULT_INTERMEDIAIRE_MARGIN_PCT,
  );
  const safeTace = Math.max(0, normalizePctInputToRate(tace, 1) ?? 1);

  const resolvedCostPerDay = safeMode === "CDI"
    ? Math.max(0, parseLocaleNumber(costPerDay) ?? computeCdiDailyCost(salaireAnnuel))
    : Math.max(0, parseLocaleNumber(costPerDay) ?? clampNumber(tjmConsultant, 500));

  const billedDays = safeMode === "CDI" ? safeDaysPerMonth * safeTace : safeDaysPerMonth;
  const commissionPerDay = computeCommissionAmount(safeTarifClient, safeIntermediaryPct);
  const netRevenuePerDay = computeNetAmount(safeTarifClient, safeIntermediaryPct);
  const grossProfitPerDay = safeTarifClient - resolvedCostPerDay;
  const netProfitPerDay = netRevenuePerDay - resolvedCostPerDay;
  const marginPct = safeTarifClient > 0 ? grossProfitPerDay / safeTarifClient : 0;
  const marginPctNet = netRevenuePerDay > 0 ? netProfitPerDay / netRevenuePerDay : 0;

  const monthly = {
    billed_days: billedDays,
    ca: safeTarifClient * billedDays,
    ca_net: netRevenuePerDay * billedDays,
    cost: resolvedCostPerDay * billedDays,
    profit_without_intermediary: grossProfitPerDay * billedDays,
    profit: netProfitPerDay * billedDays,
    intermediary_loss: commissionPerDay * billedDays,
    margin_pct: marginPct,
    margin_pct_net: marginPctNet,
  };
  const actual = {
    months: safeProjectionMonths,
    billed_days: monthly.billed_days * safeProjectionMonths,
    ca: monthly.ca * safeProjectionMonths,
    ca_net: monthly.ca_net * safeProjectionMonths,
    cost: monthly.cost * safeProjectionMonths,
    profit_without_intermediary: monthly.profit_without_intermediary * safeProjectionMonths,
    profit: monthly.profit * safeProjectionMonths,
    intermediary_loss: monthly.intermediary_loss * safeProjectionMonths,
  };
  const estimatedMonths = MONTHS_IN_YEAR - safeProjectionMonths;
  const estimated = {
    months: estimatedMonths,
    billed_days: monthly.billed_days * estimatedMonths,
    ca: monthly.ca * estimatedMonths,
    ca_net: monthly.ca_net * estimatedMonths,
    cost: monthly.cost * estimatedMonths,
    profit_without_intermediary: monthly.profit_without_intermediary * estimatedMonths,
    profit: monthly.profit * estimatedMonths,
    intermediary_loss: monthly.intermediary_loss * estimatedMonths,
  };

  const annual = {
    months: MONTHS_IN_YEAR,
    projection_months: safeProjectionMonths,
    billed_days: actual.billed_days + estimated.billed_days,
    ca: actual.ca + estimated.ca,
    ca_net: actual.ca_net + estimated.ca_net,
    cost: actual.cost + estimated.cost,
    profit_without_intermediary:
      actual.profit_without_intermediary + estimated.profit_without_intermediary,
    profit: actual.profit + estimated.profit,
    intermediary_loss: actual.intermediary_loss + estimated.intermediary_loss,
    margin_pct: monthly.margin_pct,
    margin_pct_net: monthly.margin_pct_net,
    actual,
    estimated,
  };

  return {
    mode: safeMode,
    commission_per_day: commissionPerDay,
    intermediary_impact_monthly: monthly.intermediary_loss,
    intermediary_impact_annual: annual.intermediary_loss,
    tjm_net_sold: netRevenuePerDay,
    ca_monthly_net: monthly.ca_net,
    ca_annual_net: annual.ca_net,
    margin_pct_net: marginPctNet,
    benefit_monthly_net: monthly.profit,
    benefit_annual_net: annual.profit,
    monthly,
    annual,
    series: {
      months: Array.from({ length: MONTHS_IN_YEAR }, (_, index) => ({
        index: index + 1,
        label: `M${index + 1}`,
        kind: index + 1 <= safeProjectionMonths ? "real" : "estimated",
        billed_days: monthly.billed_days,
        ca: monthly.ca,
        ca_net: monthly.ca_net,
        profit_without_intermediary: monthly.profit_without_intermediary,
        profit: monthly.profit,
        intermediary_loss: monthly.intermediary_loss,
      })),
    },
    breakdown: {
      mode: safeMode,
      cost_label: safeMode === "CDI" ? "Salaire charge" : "Consultant",
      cost: monthly.cost,
      intermediary: monthly.intermediary_loss,
      gross_margin: monthly.profit_without_intermediary,
      net_profit: monthly.profit,
      deficit: monthly.profit < 0 ? Math.abs(monthly.profit) : 0,
    },
  };
};
